'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import * as React from 'react';
import PropTypes from 'prop-types';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { unstable_composeClasses as composeClasses } from "../composeClasses/index.js";
import { getTabUtilityClass } from "./tabClasses.js";
import { useTab } from "../useTab/index.js";
import { useSlotProps } from "../utils/index.js";
import { useClassNamesOverride } from "../utils/ClassNameConfigurator.js";
import { jsx as _jsx } from "react/jsx-runtime";
var useUtilityClasses = function(ownerState) {
    var selected = ownerState.selected, disabled = ownerState.disabled;
    var slots = {
        root: [
            'root',
            selected && 'selected',
            disabled && 'disabled'
        ]
    };
    return composeClasses(slots, useClassNamesOverride(getTabUtilityClass));
};
/**
 *
 * Demos:
 *
 * - [Tabs](https://mui.com/base-ui/react-tabs/)
 *
 * API:
 *
 * - [Tab API](https://mui.com/base-ui/react-tabs/components-api/#tab)
 */ var Tab = /*#__PURE__*/ React.forwardRef(function Tab(props, forwardedRef) {
    var action = props.action, children = props.children, _props_disabled = props.disabled, disabled = _props_disabled === void 0 ? false : _props_disabled, onChange = props.onChange, onClick = props.onClick, onFocus = props.onFocus, _props_slotProps = props.slotProps, slotProps = _props_slotProps === void 0 ? {} : _props_slotProps, _props_slots = props.slots, slots = _props_slots === void 0 ? {} : _props_slots, value = props.value, other = _object_without_properties(props, [
        "action",
        "children",
        "disabled",
        "onChange",
        "onClick",
        "onFocus",
        "slotProps",
        "slots",
        "value"
    ]);
    var tabRef = React.useRef(null);
    var handleRef = useForkRef(tabRef, forwardedRef);
    var _useTab = useTab(_object_spread_props(_object_spread({}, props), {
        rootRef: handleRef,
        value: value
    })), active = _useTab.active, highlighted = _useTab.highlighted, selected = _useTab.selected, getRootProps = _useTab.getRootProps;
    var ownerState = _object_spread_props(_object_spread({}, props), {
        active: active,
        disabled: disabled,
        highlighted: highlighted,
        selected: selected
    });
    var classes = useUtilityClasses(ownerState);
    var _slots_root;
    var TabRoot = (_slots_root = slots.root) !== null && _slots_root !== void 0 ? _slots_root : 'button';
    var tabRootProps = useSlotProps({
        elementType: TabRoot,
        getSlotProps: getRootProps,
        externalSlotProps: slotProps.root,
        externalForwardedProps: other,
        additionalProps: {
            ref: forwardedRef
        },
        ownerState: ownerState,
        className: classes.root
    });
    return /*#__PURE__*/ _jsx(TabRoot, _object_spread_props(_object_spread({}, tabRootProps), {
        children: children
    }));
});
process.env.NODE_ENV !== "production" ? Tab.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * A ref for imperative actions. It currently only supports `focusVisible()` action.
   */ action: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
            current: PropTypes.shape({
                focusVisible: PropTypes.func.isRequired
            })
        })
    ]),
    /**
   * @ignore
   */ children: PropTypes.node,
    /**
   * If `true`, the component is disabled.
   * @default false
   */ disabled: PropTypes.bool,
    /**
   * Callback invoked when new value is being set.
   */ onChange: PropTypes.func,
    /**
   * The props used for each slot inside the Tab.
   * @default {}
   */ slotProps: PropTypes.shape({
        root: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ])
    }),
    /**
   * The components used for each slot inside the Tab.
   * Either a string to use a HTML element or a component.
   * @default {}
   */ slots: PropTypes.shape({
        root: PropTypes.elementType
    }),
    /**
   * You can provide your own value. Otherwise, it falls back to the child position index.
   */ value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
} : void 0;
export { Tab };
